// Lastslate editor mount.
//
// Builds the ProseMirror EditorState and EditorView, attaches it to
// the existing `#screenplay-page` DOM node, and exposes the post-
// transaction hook the renderer uses to keep `state.script.scenes`
// in sync and re-paginate.
//
//   mountEditor({ root, initialDoc, onDocChanged }): { view, dispatch, destroy }

import { EditorState, TextSelection } from 'prosemirror-state';
import { EditorView } from 'prosemirror-view';
import { keymap } from 'prosemirror-keymap';
import { baseKeymap } from 'prosemirror-commands';
import { Slice, Fragment } from 'prosemirror-model';
import * as Y from 'yjs';
import {
  ySyncPlugin,
  yUndoPlugin,
  undo as yUndo,
  redo as yRedo,
  prosemirrorToYDoc,
  initProseMirrorDoc,
} from 'y-prosemirror';
import { schema } from './schema.js';
import { buildEditorKeymap } from './commands.js';
import { buildInputRules, inferencePlugin, inferElementType } from './input-rules.js';
import { placeholderPlugin } from './placeholders.js';
import { autocompletePlugin } from './autocomplete.js';
import { elementPalettePlugin } from './element-palette.js';
import { blockIdPlugin, stampDoc } from './block-ids.js';

// Name of the Y.XmlFragment under the Y.Doc. prosemirrorToYDoc uses
// 'prosemirror' by default; we use the same name explicitly so any
// persistence/sync layer (LocalAdapter, CloudAdapter) can extract the
// same fragment without hardcoding the string in two places.
export const Y_FRAGMENT_NAME = 'prosemirror';

// A small hardcoded doc used to verify the editor end-to-end without
// loading a real script. The structure mirrors what fountainToDoc
// produces — one of each block type, in a plausible order. Stamped
// through stampDoc so the editor's "every block has a bid" invariant
// holds from the very first frame.
export function buildTestDoc() {
  const { nodes } = schema;
  return stampDoc(
    nodes.doc.create(null, [
      nodes.scene_heading.create(null, schema.text('INT. KITCHEN - NIGHT')),
      nodes.action.create(
        null,
        schema.text('Rain hammers the windows. JANE stands at the sink, hands wet.'),
      ),
      nodes.character.create(null, schema.text('JANE')),
      nodes.parenthetical.create(null, schema.text('(to herself)')),
      nodes.dialogue.create(null, schema.text("It's louder than last time.")),
      nodes.shot.create(null, schema.text('ANGLE ON THE WINDOW')),
      nodes.action.create(null, schema.text('A shadow moves across the glass.')),
      nodes.transition.create(null, schema.text('CUT TO:')),
    ]),
  );
}

// Build the Y.Doc + EditorState pair. Two seed paths:
//   - `initialYUpdate` provided (cache hit): apply the binary update
//     to a fresh Y.Doc, then derive the PM doc from Y. This preserves
//     stable block IDs across reopens and gives the LocalAdapter
//     load-from-disk semantics for free-tier users.
//   - otherwise (cache miss / new script): stamp the PM doc and seed
//     Y from it. First time the file is opened, the source of truth
//     is still the parsed Fountain — Yjs gets a fresh state derived
//     from it and is persisted on the first save.
//
// ySyncPlugin owns the doc state from this point — every PM
// transaction propagates to Y, every Y change propagates back as a
// PM transaction. yUndoPlugin replaces prosemirror-history so
// undo/redo respect CRDT semantics (future collaborative edits
// won't undo other writers' work).
function buildStateAndDoc({ initialDoc, initialYUpdate, ydoc: externalYDoc } = {}) {
  let ydoc;
  let pmDoc;
  if (externalYDoc) {
    // Phase 1.3 entry: the Y.Doc was constructed and hydrated upstream
    // (by doc-store, which attached y-indexeddb and the Hocuspocus
    // provider before mount). Derive the PM doc from its existing
    // XmlFragment so ySyncPlugin doesn't fire a redundant initial
    // Y→PM transaction. The providers stay attached for the
    // EditorView's entire lifetime.
    ydoc = externalYDoc;
    const yFragment = ydoc.get(Y_FRAGMENT_NAME, Y.XmlFragment);
    const result = initProseMirrorDoc(yFragment, schema);
    pmDoc = stampDoc(result.doc);
  } else if (initialYUpdate && initialYUpdate.length > 0) {
    ydoc = new Y.Doc();
    Y.applyUpdate(ydoc, initialYUpdate);
    const yFragment = ydoc.get(Y_FRAGMENT_NAME, Y.XmlFragment);
    const result = initProseMirrorDoc(yFragment, schema);
    // stampDoc is idempotent — a cached doc already has bids; this is
    // belt-and-braces in case the cache predates the bid invariant.
    pmDoc = stampDoc(result.doc);
  } else {
    pmDoc = stampDoc(initialDoc || buildTestDoc());
    ydoc = prosemirrorToYDoc(pmDoc, Y_FRAGMENT_NAME);
  }
  const yFragment = ydoc.get(Y_FRAGMENT_NAME, Y.XmlFragment);
  const state = EditorState.create({
    schema,
    // Pass the stamped PM doc explicitly. Y and PM are already in
    // sync at frame zero — no Y → PM transaction will fire on
    // view-mount, and the editor renders content immediately instead
    // of starting empty and getting populated on the next tick
    // (which Playwright smoke specs measure before).
    doc: pmDoc,
    plugins: [
      ySyncPlugin(yFragment),
      yUndoPlugin(),
      keymap({ 'Mod-z': yUndo, 'Shift-Mod-z': yRedo, 'Mod-y': yRedo }),
      // Autocomplete + element palette plugins sit BEFORE the editor
      // keymap so they can intercept Tab/Enter/letter keys when their
      // popovers are open. The palette is exclusive — when it's open
      // the autocomplete suggester is irrelevant — so order between
      // these two doesn't matter in practice; alphabetical for stability.
      autocompletePlugin(),
      elementPalettePlugin(),
      keymap(buildEditorKeymap()),
      buildInputRules(),
      inferencePlugin(),
      placeholderPlugin(),
      // Block-ID backfill: keeps the bid invariant after splits,
      // pastes, and any future command that creates blocks without
      // pre-stamping. Sits after inference so type changes settle
      // first; the meta-tag on its output keeps inference from
      // re-running on the silent stamp transaction.
      blockIdPlugin(),
      keymap(baseKeymap),
    ],
  });
  return { state, ydoc, yFragment };
}

const STORAGE_TO_NODE = {
  'scene-heading': 'scene_heading',
  action: 'action',
  character: 'character',
  parenthetical: 'parenthetical',
  dialogue: 'dialogue',
  transition: 'transition',
  shot: 'shot',
  centered: 'centered',
};

// Build a Slice from plain text by inferring each line's block type
// via the canonical inference rules. Used as the view's
// `transformPasted` so pasting plain text from outside the app yields
// properly-classified blocks instead of one giant action block.
function pastedTextToSlice(text) {
  const lines = text.split(/\r?\n/);
  const nodes = [];
  let prevStorage = null;
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) {
      prevStorage = null;
      continue;
    }
    const inferred = inferElementType(line, prevStorage) || 'action';
    const storage = inferred;
    let text = line;
    if (storage === 'parenthetical') {
      text = line.replace(/^\(|\)$/g, '');
    }
    const nodeName = STORAGE_TO_NODE[storage] || 'action';
    const nodeType = schema.nodes[nodeName];
    nodes.push(nodeType.create(null, text ? schema.text(text) : null));
    prevStorage = storage;
  }
  if (nodes.length === 0) return null;
  return new Slice(Fragment.from(nodes), 1, 1);
}

export function mountEditor({
  root,
  initialDoc,
  initialYUpdate,
  ydoc: externalYDoc,
  onDocChanged,
  onYUpdate,
} = {}) {
  if (!root || root.nodeType !== 1) {
    throw new Error('editor/mount: root must be an HTMLElement');
  }

  // ProseMirror manages contenteditable itself — including the DOM
  // children of the mount node. Clearing any pre-existing content
  // avoids fighting the legacy renderer if it painted into the page
  // before we got here.
  while (root.firstChild) root.removeChild(root.firstChild);

  const { state, ydoc, yFragment } = buildStateAndDoc({
    initialDoc,
    initialYUpdate,
    ydoc: externalYDoc,
  });

  // LocalAdapter / CloudAdapter hook. Every CRDT update fires this
  // callback synchronously; the adapter is expected to debounce
  // before flushing to disk or wire.
  if (typeof onYUpdate === 'function') {
    ydoc.on('update', onYUpdate);
  }

  // Pass `{ mount: root }` so PM uses #screenplay-page AS the editable
  // element rather than appending a <div class="ProseMirror"> wrapper
  // inside it. The legacy renderer, the pagination DOM walker, the UI
  // test helpers, and the export pipelines all assume the block divs
  // are direct children of #screenplay-page — mount: preserves that
  // invariant.
  const view = new EditorView(
    { mount: root },
    {
      state,
      dispatchTransaction(tr) {
        // `this` is the EditorView, bound by PM even when this fires
        // re-entrantly during construction. Going through `this`
        // (rather than the outer `view` const) lets ySyncPlugin's
        // initial Y → PM sync transaction land cleanly — that
        // transaction fires before `view` finishes assignment, so a
        // closure reference would hit the temporal dead zone and
        // throw "Cannot access 'view' before initialization".
        // Strip PM's per-keystroke auto-scrollIntoView. baseKeymap
        // commands (Enter, Backspace, Delete, joinBackward, etc.) all
        // call tr.scrollIntoView() before dispatching — the source of
        // per-key scroll drift, especially noticeable at high zoom.
        // A debounced caret-visibility pass in app.js fires once
        // typing settles, scrolling the caret back into view if it
        // actually went off-screen. PM's UPDATED_SCROLL bit is 4 in
        // prosemirror-state; clearing it before state.apply() keeps
        // the resulting state's scrollToSelection counter unchanged
        // so view.updateState doesn't scroll either.
        tr.updated &= ~4;

        const newState = this.state.apply(tr);
        this.updateState(newState);

        if (tr.docChanged && typeof onDocChanged === 'function') {
          try {
            onDocChanged(newState, tr);
          } catch (err) {
            console.error('[editor/mount] onDocChanged threw:', err);
          }
        }
      },
      // External-paste transformer. PM's default handles intra-editor
      // copy/paste via the schema's parseDOM/toDOM; plain text from
      // outside the app gets routed through inference here so each line
      // lands as the right block type.
      transformPastedText(text) {
        void text;
        return text; // Let transformPasted handle the structural work.
      },
      transformPasted(slice) {
        // Heuristic: if every leaf in the slice is a text node with no
        // marks (a "plain text" paste), re-build the slice through
        // inference. Intra-editor pastes preserve their block structure
        // and skip the inference path.
        let isPlain = true;
        slice.content.descendants((node) => {
          if (node.isText) return true;
          if (node.isBlock && node.type === schema.nodes.action) return true;
          if (node.isBlock) {
            isPlain = false;
            return false;
          }
          return true;
        });
        if (!isPlain) return slice;
        const text = slice.content.textBetween(0, slice.content.size, '\n');
        const inferred = pastedTextToSlice(text);
        return inferred || slice;
      },
    },
  );

  // Swap the entire document contents in place. ySyncPlugin observes
  // the PM transaction and mirrors the change into the Y.Doc, so the
  // CRDT history of the previous script is replaced by the new one —
  // exactly what we want when the user opens a different file. (For
  // an active cloud session, this would NOT be the right call; the
  // CloudAdapter swaps Y.Doc identity instead.)
  function setDoc(newDoc) {
    const stamped = stampDoc(newDoc);
    const tr = view.state.tr.replaceWith(0, view.state.doc.content.size, stamped.content);
    tr.setSelection(TextSelection.atStart(tr.doc));
    view.dispatch(tr);
  }

  return {
    view,
    dispatch: view.dispatch.bind(view),
    setDoc,
    // Yjs surface for persistence/sync adapters. LocalAdapter listens
    // for `ydoc.on('update', ...)` and streams binary updates to disk.
    // CloudAdapter pipes the same stream into a y-websocket provider.
    ydoc,
    yFragment,
    destroy() {
      ydoc.destroy();
      view.destroy();
    },
  };
}
